import { Select } from "antd"
import FormItem from "antd/lib/form/FormItem"
import React, { CSSProperties, FC, memo, useEffect, useState } from "react"
import {
    GetBadgesQuery,
    GetUpdatedBookerQuery,
    NewBookingInput,
    OptionalInputFieldsInput,
    SettingEndUserApp,
    SettingEndUserAppQuery,
    useGetBadgesQuery,
    useGetCompanyQuery,
} from "../../../../generated/graphql"
import { useMediaSize } from "../../../../layouts/Main/GetWindowDimensions/GetWindowDimensions"
import LayoutForm from "../../../../views/LayoutForm/LayoutForm"

import { NewBookingInputKeys } from "../../BookingSummary/BookingSummary"
import css from "../StepSummary2.module.css"
import NewsLetterInput from "./OptionalInputFields/NewsLetterInput"
import AGBInput from "./OptionalInputFields/AGBInput"
import PrivacyStatementInput from "./OptionalInputFields/PrivacyStatementInput"
import CustomInput from "./CustomInput"
import FoundUsInput from "./OptionalInputFields/FoundUsInput"

type OptionalInputFieldsProps = {
    booker: GetUpdatedBookerQuery["booker"]
    bookingData?: NewBookingInput
    updateBookingData: (key: NewBookingInputKeys, value: any) => void
    appSettings?: SettingEndUserAppQuery["settingEndUserAppPublic"]
    showAlert?: boolean
    inputFieldValues: Array<{ [Key: string]: string }>
    setInputFieldValues: (values: Array<{ [Key: string]: string }>) => void
}

const OptionalInputFields: FC<OptionalInputFieldsProps> = ({
    booker,
    bookingData,
    updateBookingData,
    appSettings,
    showAlert,
    inputFieldValues,
    setInputFieldValues,
}) => {
    const mediaSize = useMediaSize()
    const { media } = mediaSize
    const [optionalInputFields, setOptionalInputFields] = useState<SettingEndUserApp["optionalInputFields"]>([
        { name: "test" },
    ])
    const [additionalInfoJSON, setAdditionalInfoJSON] = useState<any>(JSON.parse(bookingData?.additionalInfo || "{}"))

    const { data: companyData } = useGetCompanyQuery()
    const companySettings = companyData?.companyPublic

    useEffect(() => {
        const updatedFields: Array<OptionalInputFieldsInput> = []
        appSettings?.optionalInputFields?.forEach((field: OptionalInputFieldsInput) => {
            if (field.enabeld) {
                updatedFields.push(field)
            }

            setOptionalInputFields(updatedFields)
        })
    }, [appSettings])

    const BadgesInput = ({
        field,
        bookingData,
        style,
    }: {
        field: OptionalInputFieldsInput
        bookingData?: NewBookingInput
        style?: CSSProperties
    }) => {
        const { data: companyBadgeData } = useGetBadgesQuery()
        const companybadges: GetBadgesQuery["badges"] = companyBadgeData?.badges ?? []
        const updateAdditionalInfo = (selectedBadge?: string) => {
            const updatedAdditionalInfoJSON = { ...additionalInfoJSON }

            if (field.label) {
                if (!selectedBadge) {
                    delete updatedAdditionalInfoJSON[field.label]
                } else if (field.label && selectedBadge) {
                    updatedAdditionalInfoJSON[field.label] = selectedBadge
                }

                updateBookingData("additionalInfo", JSON.stringify(updatedAdditionalInfoJSON))
                setAdditionalInfoJSON(updatedAdditionalInfoJSON)
                // handleInputFieldValues([{ name: field.label, value: selectedBadge }])
            }
        }

        return (
            <FormItem label={field.label} style={style} key={field.name}>
                <Select
                    onSelect={(e: string) => updateAdditionalInfo(e)}
                    // value={selectedBadge}
                    value={additionalInfoJSON[field.label ?? ""]}
                    // placeholder={`Keine ${field.label}`}
                    placeholder={field.placeholder}
                >
                    <Select.Option /* key={null} */ value={undefined}>{`Keine ${field.label}`}</Select.Option>
                    {companybadges.map((badge) => {
                        return (
                            <Select.Option key={badge.id} value={badge.name}>
                                {badge.name}
                            </Select.Option>
                        )
                    })}
                </Select>
                <div className={css.inputDescription}>{field.description}</div>
            </FormItem>
        )
    }

    const columns = media === "xs" ? 1 : 2
    const standardInputFields = ["newsletter", "agb", "privacyStatement", "foundUs"]
    const noneStandardFields = optionalInputFields?.filter(
        (field) => !standardInputFields.includes(field.name) && field.name !== undefined
    )
    const nonCheckboxGroupFields = noneStandardFields?.filter((field) => field.type !== "CheckboxGroup" && field.type !== "RadioGroup") ?? []
    const checkboxGroupFields = noneStandardFields?.filter((field) => field.type === "CheckboxGroup" || field.type === "RadioGroup") ?? []
    const onlyOneNonCheckboxGroupField = nonCheckboxGroupFields.length === 1

    const handleInputFieldValues = (values: Array<{ [Key: string]: string | boolean | undefined }>) => {
        // update inputFieldValues with new values and set it to state
        const newInputFieldValues = [...inputFieldValues]

        values.forEach((value) => {
            // Der Key ist der Feldname (z.B. "checkboxGroup1" oder "Optionen über Optionen")
            const valueKey = Object.keys(value)[0]
            const index = newInputFieldValues.findIndex((field) => Object.keys(field)[0] === valueKey)
            if (index !== -1) {
                newInputFieldValues[index] = Object.fromEntries(
                    Object.entries(value).filter(([_, v]) => v !== undefined)
                ) as { [Key: string]: string }
            } else {
                newInputFieldValues.push(
                    Object.fromEntries(Object.entries(value).filter(([_, v]) => v !== undefined)) as {
                        [Key: string]: string
                    }
                )
            }
        })

        setInputFieldValues(newInputFieldValues)
    }

    return (
        <LayoutForm columns={columns}>
            {/* Nicht-CheckboxGroup Felder */}
            {optionalInputFields?.map((field: OptionalInputFieldsInput) => {
                if (!field.name || field.type === "CheckboxGroup" || field.type === "RadioGroup") return null
                if (field.name === "badges") {
                    return (
                        <div key={field.name}>
                            <BadgesInput
                                field={field}
                                bookingData={bookingData}
                                style={{ gridColumn: onlyOneNonCheckboxGroupField ? `span ${columns}` : "span 1" }}
                            />
                        </div>
                    )
                } else if (!standardInputFields.includes(field.name)) {
                    const gridColumn = onlyOneNonCheckboxGroupField ? `span ${columns}` : "span 1"
                    return (
                        <div
                            key={field.name}
                            style={{ gridColumn }}
                        >
                            <CustomInput
                                field={field}
                                bookingData={bookingData}
                                additionalInfoJSON={additionalInfoJSON}
                                setAdditionalInfoJSON={setAdditionalInfoJSON}
                                updateBookingData={updateBookingData}
                                style={{ gridColumn }}
                                handleInputFieldValues={handleInputFieldValues}
                                showAlert={showAlert}
                            />
                        </div>
                    )
                } else {
                    return null
                }
            })}

            {/* CheckboxGroup Felder in eigenem Grid-Container */}
            {checkboxGroupFields.length > 0 && (
                <div className={css.checkboxGroupGrid} style={{ gridColumn: `span ${columns}` }}>
                    {checkboxGroupFields.map((field) => (
                        <div key={field.name} className={css.checkboxGroupGridItem}>
                            <CustomInput
                                field={field}
                                bookingData={bookingData}
                                additionalInfoJSON={additionalInfoJSON}
                                setAdditionalInfoJSON={setAdditionalInfoJSON}
                                updateBookingData={updateBookingData}
                                handleInputFieldValues={handleInputFieldValues}
                                showAlert={showAlert}
                            />
                        </div>
                    ))}
                </div>
            )}

            {optionalInputFields?.some((field) => field.name === "agb") && (
                <div key={"agb"} style={{ gridColumn: `span ${columns}` }}>
                    <AGBInput
                        field={optionalInputFields.filter((field) => field.name === "agb")[0]}
                        bookingData={bookingData}
                        updateBookingData={updateBookingData}
                        // style={{ gridColumn: `span ${columns}` }}
                        showAlert={showAlert}
                        companySettings={companySettings}
                    />
                </div>
            )}
            {optionalInputFields?.some((field) => field.name === "privacyStatement") && (
                <div key={"privacyStatement"} style={{ gridColumn: `span ${columns}` }}>
                    <PrivacyStatementInput
                        field={optionalInputFields.filter((field) => field.name === "privacyStatement")[0]}
                        bookingData={bookingData}
                        updateBookingData={updateBookingData}
                        // style={{ gridColumn: `span ${columns}` }}
                        showAlert={showAlert}
                        companySettings={companySettings}
                    />
                </div>
            )}
            {optionalInputFields?.some((field) => field.name === "newsletter") && (
                <div key={"newsLetter"} style={{ gridColumn: `span ${columns}` }}>
                    <NewsLetterInput
                        field={optionalInputFields.filter((field) => field.name === "newsletter")[0]}
                        booker={booker}
                        style={{ gridColumn: `span ${columns}` }}
                    />
                </div>
            )}
            {optionalInputFields?.some((field) => field.name === "foundUs") && (
                <div key={"foundUs"} style={{ gridColumn: `span ${columns}` }}>
                    <FoundUsInput
                        field={optionalInputFields.filter((field) => field.name === "foundUs")[0]}
                        bookingData={bookingData}
                        updateBookingData={updateBookingData}
                        style={{ gridColumn: `span ${columns}` }}
                    />
                </div>
            )}
        </LayoutForm>
    )
}

export default memo(OptionalInputFields)
